import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { BookingService } from "@/services/BookingService";
import { addDays, addMonths, addWeeks, format, isAfter, isBefore, startOfDay } from "date-fns";
import { Info } from "lucide-react";
import { useSlotHolidayFlow } from "./SlotHolidayProvider";

type SelectDatesScreenProps = {
  onClose?: () => void;
};

// R25 (hearth/docs/BOOKING_RULES.md#r25) — a slot holiday needs ≥7 days' notice; dates within the
// next 7 days are not selectable. The server is the authority; this is the matching UX guard.
const SLOT_HOLIDAY_NOTICE_DAYS = 7;

// Represents a future slot booking occurrence
type SlotBooking = {
  id: string;
  date: Date;
  startTime: string;
  endTime: string;
  roomName: string;
};

// Generate future booking occurrences based on the slot booking's recurrence pattern
export const generateBookingOccurrences = (
  baseDate: Date,
  startTime: string,
  endTime: string,
  roomName: string,
  recurrence?: {
    frequency: "daily" | "weekly" | "monthly";
    interval?: number;
    count?: number;
    until?: Date;
    cancelledFrom?: Date | string | null;
    byWeekday?: string[];
    exceptions?: (Date | string)[];
  },
): SlotBooking[] => {
  const bookings: SlotBooking[] = [];
  const today = startOfDay(new Date());

  // Default to 52 weeks if no recurrence specified (slot bookings are typically weekly)
  const maxOccurrences = recurrence?.count || 52;
  const until = recurrence?.until;
  // Termination bound — no holiday date may be offered on/after the from-date cancellation.
  const cancelledFrom = recurrence?.cancelledFrom ? startOfDay(new Date(recurrence.cancelledFrom)) : null;
  const interval = recurrence?.interval || 1;
  const frequency = recurrence?.frequency || "weekly";
  const exceptions = recurrence?.exceptions || [];

  let currentDate = new Date(baseDate);
  let occurrenceCount = 0;

  // Start from the next occurrence after today
  while (isBefore(currentDate, today)) {
    if (frequency === "weekly") {
      currentDate = addWeeks(currentDate, interval);
    } else if (frequency === "daily") {
      currentDate = addDays(currentDate, interval);
    } else if (frequency === "monthly") {
      currentDate = addMonths(currentDate, interval);
    }
  }

  while (occurrenceCount < maxOccurrences) {
    // Stop at the until date OR the termination (cancelledFrom) date — no occurrence on/after either.
    if (until && isAfter(currentDate, until)) {
      break;
    }
    if (cancelledFrom && !isBefore(startOfDay(currentDate), cancelledFrom)) {
      break;
    }

    // Check if this date is in the exceptions list
    const isException = exceptions.some(
      (exceptionDate) => startOfDay(new Date(exceptionDate)).getTime() === startOfDay(currentDate).getTime(),
    );

    if (!isException && !isBefore(currentDate, today)) {
      bookings.push({
        id: `occurrence-${currentDate.getTime()}`,
        date: new Date(currentDate),
        startTime,
        endTime,
        roomName,
      });
      occurrenceCount++;
    }

    // Move to next occurrence
    if (frequency === "weekly") {
      currentDate = addWeeks(currentDate, interval);
    } else if (frequency === "daily") {
      currentDate = addDays(currentDate, interval);
    } else if (frequency === "monthly") {
      currentDate = addMonths(currentDate, interval);
    }
  }

  return bookings;
};

const SelectDatesScreen = ({ onClose }: SelectDatesScreenProps) => {
  const { setStep, selectedBookingIds, setSelectedBookingIds, resetFlow, booking, setIsLoading, setError, error } =
    useSlotHolidayFlow();

  const bookingService = useMemo(() => new BookingService(), []);

  // Generate bookings based on the actual booking data
  const bookings = useMemo(() => {
    if (!booking) return [];

    const recurrence = booking.recurrence || booking.recurrencePattern;

    // Transform the recurrence to match the expected type
    const transformedRecurrence = recurrence
      ? {
          frequency: recurrence.frequency,
          interval: recurrence.interval,
          count: recurrence.count,
          until: recurrence.until,
          cancelledFrom: recurrence.cancelledFrom, // stop offering dates past a termination
          byWeekday: recurrence.byWeekday,
          exceptions: recurrence.exceptions,
        }
      : undefined;

    return generateBookingOccurrences(
      booking.date,
      booking.startTime,
      booking.endTime,
      booking.room?.name || "Practice Room",
      transformedRecurrence,
    );
  }, [booking]);

  // R25 — earliest date a holiday may start (today + 7 days, start-of-day). Dates before this are
  // too soon and cannot be selected.
  const earliestHolidayDate = useMemo(() => addDays(startOfDay(new Date()), SLOT_HOLIDAY_NOTICE_DAYS), []);
  const isTooSoon = (date: Date) => isBefore(startOfDay(date), earliestHolidayDate);

  const handleToggleBooking = (bookingId: string) => {
    const occ = bookings.find((b) => b.id === bookingId);
    if (occ && isTooSoon(occ.date)) return; // R25 — within the 7-day notice window, not selectable
    setSelectedBookingIds(
      selectedBookingIds.includes(bookingId)
        ? selectedBookingIds.filter((id) => id !== bookingId)
        : [...selectedBookingIds, bookingId],
    );
  };

  // Shared row renderer for the desktop + mobile occurrence lists (keeps the R25 gate in one place).
  const renderOccurrenceRow = (occ: SlotBooking) => {
    const isSelected = selectedBookingIds.includes(occ.id);
    const tooSoon = isTooSoon(occ.date);
    return (
      <div
        key={occ.id}
        data-testid={`holiday-occurrence-${format(occ.date, "yyyy-MM-dd")}`}
        className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
          tooSoon
            ? "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200"
            : isSelected
              ? "bg-indigo-50 border-indigo-300 cursor-pointer"
              : "bg-white border-gray-200 hover:border-indigo-200 cursor-pointer"
        }`}
        onClick={tooSoon ? undefined : () => handleToggleBooking(occ.id)}
      >
        <Checkbox
          checked={isSelected}
          disabled={tooSoon}
          onCheckedChange={() => handleToggleBooking(occ.id)}
          aria-label={`Select booking for ${format(occ.date, "EEEE do MMMM yyyy")}`}
        />
        <div className="flex-1">
          <p className="font-medium text-sm">{format(occ.date, "EEEE do MMMM yyyy")}</p>
          <p className="text-xs text-gray-600">
            {occ.startTime} - {occ.endTime} • {occ.roomName}
          </p>
          {tooSoon && <p className="mt-0.5 text-xs text-amber-700">Less than 7 days&apos; notice</p>}
        </div>
      </div>
    );
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  const handleNext = async () => {
    if (selectedBookingIds.length === 0 || !booking) return;

    try {
      // Extract the dates from the selected booking IDs
      const selectedDatesISO = bookings
        .filter((b) => selectedBookingIds.includes(b.id))
        .map((b) => format(b.date, "yyyy-MM-dd"));

      // Set loading state and move to loading screen
      setIsLoading(true);
      setStep(2);

      // Call the API to add slot holiday
      await bookingService.api.addSlotHoliday(booking.id, selectedDatesISO);

      // Move to confirmation screen
      setIsLoading(false);
      setStep(3);
    } catch (error) {
      console.error("Failed to add slot holiday:", error);
      setIsLoading(false);
      setError(error instanceof Error ? error.message : "Failed to add slot holiday");
      // Return to the date-selection screen (step 1) so the error banner is shown. Without this the
      // flow is left on step 2 with isLoading=false, which the container maps to its "Unknown State"
      // fallback (there is no step-2-not-loading screen). Mirrors AdminSelectDatesScreen.handleNext.
      setStep(1);
    }
  };

  // Get selected booking dates for display
  const selectedDates = bookings
    .filter((b) => selectedBookingIds.includes(b.id))
    .map((b) => format(b.date, "EEEE do MMMM yyyy"));

  // If no booking data is available, show error state
  if (!booking) {
    return (
      <div className="min-h-[50vh] w-full flex flex-col items-center justify-center bg-white rounded-xl my-4 p-6">
        <p className="text-gray-600 text-center mb-4">No booking data available.</p>
        <Button variant="outline" onClick={handleCancel}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className={`min-h-[50vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4`}>
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50 rounded-t-2xl">
        <h2 className="text-2xl font-semibold mb-2 text-center w-full">Select Slot Holiday Dates</h2>
        <p className="text-gray-600 text-xs text-center w-full">
          Choose the dates you wish to take a slot holiday for.
        </p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1 w-full">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Select Slot Holiday Dates</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Choose the dates you wish to take a slot holiday for your {booking.room?.name || "room"} booking.
          </p>
          <div className="mt-4 p-3 bg-white/60 rounded-lg">
            <p className="text-xs font-semibold text-gray-700">Booking Details:</p>
            <p className="text-sm text-gray-600 mt-1">{booking.room?.name}</p>
            <p className="text-sm text-gray-600">{booking.location?.name}</p>
            <p className="text-sm text-gray-600">
              {booking.startTime.substring(0, 2)}:{booking.startTime.substring(2, 4)} -{" "}
              {booking.endTime.substring(0, 2)}:{booking.endTime.substring(2, 4)}
            </p>
          </div>

          {/* Selected dates summary */}
          {selectedBookingIds.length > 0 && (
            <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-200">
              <h3 className="font-semibold text-sm mb-2">Selected Dates ({selectedBookingIds.length})</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedDates.map((date, index) => (
                  <p key={index} className="text-xs text-gray-700">
                    {date}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 mt-4 mx-4 p-3 bg-amber-50 border border-amber-200 rounded-lg max-w-xl">
            <Info className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Slot Holidays require a minimum of 7 days' notice. If you take a Slot Holiday and later wish to rebook the
              same space, we cannot guarantee that it will still be available. Any replacement bookings will be charged
              at the standard flexible booking rate.
            </p>
          </div>
        </div>

        {/* Right column - Booking selection */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          <div className="mb-4">
            <h3 className="font-semibold text-lg mb-2">Upcoming Bookings</h3>
            <p className="text-sm text-gray-600">Select the bookings you want to pause</p>
          </div>

          {/* Booking list */}
          <div className="space-y-2 max-h-96 overflow-y-auto mb-6 pr-2">
            {bookings.map(renderOccurrenceRow)}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            <Button
              className={`w-full ${defaultButtonClasses}`}
              onClick={handleNext}
              disabled={selectedBookingIds.length === 0}
              aria-label="Proceed to next step"
            >
              Next ({selectedBookingIds.length} selected)
            </Button>
            <Button
              variant="outline"
              className={`w-full ${secondaryButtonClasses}`}
              onClick={handleCancel}
              aria-label="Cancel and close dialog"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6 w-full min-w-screen">
        <div className="mb-4">
          <h3 className="font-semibold text-lg mb-2">Upcoming Bookings</h3>
          <p className="text-sm text-gray-600">Select the bookings you want to pause</p>
        </div>

        {/* Booking list */}
        <div className="space-y-2 max-h-80 overflow-y-auto mb-6">
          {bookings.map(renderOccurrenceRow)}
        </div>

        {/* Selected dates summary */}
        {selectedBookingIds.length > 0 && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-sm mb-2">Selected Dates ({selectedBookingIds.length})</h3>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {selectedDates.map((date, index) => (
                <p key={index} className="text-xs text-gray-700">
                  {date}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          <Button
            className={`w-full ${defaultButtonClasses}`}
            onClick={handleNext}
            disabled={selectedBookingIds.length === 0}
            aria-label="Proceed to next step"
          >
            Next ({selectedBookingIds.length} selected)
          </Button>
          <Button
            variant="outline"
            className={`w-full ${secondaryButtonClasses}`}
            onClick={handleCancel}
            aria-label="Cancel and close dialog"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectDatesScreen;
